import { create } from "zustand";

/** A pending row edit to a single stipulation, keyed by `StipulationView.id`. */
export interface StipulationEdit {
  actionCode?: string;
  reasonPk?: number;
  /**
   * Snapshot of the selected action's `requiresNote`, taken at selection
   * time rather than re-derived later via a `stip.availableActions` lookup —
   * some actions have no self-loop in the state machine, so the just-applied
   * action can drop out of that list the moment the save response lands.
   */
  requiresNote?: boolean;
  note?: string;
  porExceptionChecked?: boolean;
}

interface StipulationEditsState {
  /**
   * Pending edits keyed by `StipulationView.id` (the `stipCode` + `applicantPk`
   * composite) rather than `stipCode` alone, so two applicants sharing the same
   * `stipCode` (e.g. Proof of Income for both) don't collide on one entry.
   * Empty until the analyst touches a row.
   */
  edits: Record<string, StipulationEdit>;
  /** Records the selected action, its requiresNote snapshot, and optional reason for a stipulation. */
  setAction: (id: string, actionCode: string, requiresNote: boolean, reasonPk?: number) => void;
  /** Records the dealer-instruction note text for a stipulation. */
  setNote: (id: string, note: string) => void;
  /** Records whether secondary proofs (POR exception) is checked for a stipulation. */
  setPorException: (id: string, checked: boolean) => void;
  /** Clears a single row's pending edit after a successful row save. */
  clear: (id: string) => void;
  /** Clears all pending edits (e.g. after a successful main Save, or on deal switch). */
  reset: () => void;
}

/**
 * Holds in-progress stipulation row state (selected action, reason, dealer
 * note) while the analyst interacts with a row. Successful row saves clear the
 * entry; `reset()` still clears any in-progress state on deal switch.
 */
export const useStipulationEditsStore = create<StipulationEditsState>((set) => ({
  edits: {},
  setAction: (id, actionCode, requiresNote, reasonPk) =>
    set((state) => ({
      edits: { ...state.edits, [id]: { ...state.edits[id], actionCode, requiresNote, reasonPk } },
    })),
  setNote: (id, note) =>
    set((state) => ({
      edits: { ...state.edits, [id]: { ...state.edits[id], note } },
    })),
  setPorException: (id, checked) =>
    set((state) => ({
      edits: { ...state.edits, [id]: { ...state.edits[id], porExceptionChecked: checked } },
    })),
  clear: (id) =>
    set((state) => {
      const { [id]: _removed, ...rest } = state.edits;
      return { edits: rest };
    }),
  reset: () => set({ edits: {} }),
}));

/**
 * Selects the pending edit for a single stipulation (stable empty object when
 * untouched so consumers can read `.actionCode` / `.note` without guards).
 * @param {string} id - The stipulation's `StipulationView.id`.
 * @returns {StipulationEdit} The pending edit, or an empty object.
 */
export function useStipulationEdit(id: string): StipulationEdit {
  return useStipulationEditsStore((state) => state.edits[id] ?? EMPTY_EDIT);
}

const EMPTY_EDIT: StipulationEdit = {};
